import { useCallback, useEffect, useState } from 'react';
import { fetchApiRef, useApi } from '@backstage/frontend-plugin-api';

/**
 * GETs a backend plugin route (`plugin://<pluginId><path>`); `reload` fetches it again, and
 * `refreshMs` refetches it periodically while the tab is visible.
 */
export function useBackend<T>(
  pluginId: string,
  path: string | undefined,
  options: { refreshMs?: number } = {},
) {
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<{ value?: T; error?: Error }>({});
  const [version, setVersion] = useState(0);
  const { refreshMs } = options;

  useEffect(() => {
    if (!path) return undefined;
    let cancelled = false;
    fetchApi
      .fetch(`plugin://${pluginId}${path}`)
      .then(async res => {
        if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
        return res.json();
      })
      .then(value => !cancelled && setState({ value }))
      .catch(error => !cancelled && setState(s => ({ ...s, error })));
    return () => {
      cancelled = true;
    };
  }, [fetchApi, pluginId, path, version]);

  useEffect(() => {
    if (!refreshMs) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') setVersion(v => v + 1);
    }, refreshMs);
    return () => clearInterval(timer);
  }, [refreshMs]);

  const reload = useCallback(() => setVersion(v => v + 1), []);
  return { ...state, loading: !state.value && !state.error, reload };
}

/** GETs a platform-orchestrator route; `reload` fetches it again. */
export function useOrchestrator<T>(path: string | undefined) {
  return useBackend<T>('platform-orchestrator', path);
}
